import { useEffect, useState, useRef } from "react";
import CryptoJS from "crypto-js";
import BaseEndpoint from "../../../endpoints/baseEndpoint";
import useAuthUser from "../../../hooks/useAuthUser";
import { useHistory } from "react-router-dom";

const getStoredAnswer = () => {
    const raw = localStorage.getItem('answer');
    if (!raw) return null;
    try {
        const decrypted = CryptoJS.AES.decrypt(raw, 'secret').toString(CryptoJS.enc.Utf8);
        return JSON.parse(decrypted);
    } catch (e) {
        return null;
    }
};

const getStoredMapping = () => {
    const raw = localStorage.getItem('mapping');
    if (!raw) return null;
    try {
        const decrypted = CryptoJS.AES.decrypt(raw, 'secret').toString(CryptoJS.enc.Utf8);
        return JSON.parse(decrypted);
    } catch (e) {
        return null;
    }
};

/**
 * SPT Potensi Kerja Layout
 * Similar to PT Short layout - auto-advance without left navigation
 * Supports both TEXT and IMAGE modes
 */
export default function PotensiKerjaLayout({ session, current, countdown, nextSession }) {
    const router = useHistory();
    const { user } = useAuthUser();
    const [answerData, setAnswerData] = useState(() => getStoredAnswer());
    const [mappingData, setMappingData] = useState(() => getStoredMapping());

    // Spam click & double submission locks
    const isAdvancingRef = useRef(false);
    const isSubmittingRef = useRef(false);
    const advanceTimeoutRef = useRef(null);
    const [isLocked, setIsLocked] = useState(false);

    // Reset lock whenever the question or session advances legitimately
    useEffect(() => {
        isAdvancingRef.current = false;
        setIsLocked(false);
    }, [current.data.soal, current.data.session]);

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (advanceTimeoutRef.current) {
                clearTimeout(advanceTimeoutRef.current);
            }
        };
    }, []);

    // Collect mapping data from session content (for shuffled options)
    useEffect(() => {
        if (!session.data || session.data.length === 0) return;
        if (current.data.session === null) return;
        const currentSession = session.data[current.data.session];
        if (!currentSession || currentSession.type !== 'exam') return;

        let maps = (getStoredMapping() || mappingData) ? { ...(getStoredMapping() || mappingData) } : {};
        (currentSession.content || []).forEach((item) => {
            if (item.mapping) {
                maps[item.id] = item.mapping;
            }
        });

        setMappingData(maps);
        localStorage.setItem('mapping', CryptoJS.AES.encrypt(JSON.stringify(maps), 'secret').toString());
    }, [session, current.data.session]);

    // Cache master data per kolom — master should only change when kolom changes, not per question
    const [cachedMasterByKolom, setCachedMasterByKolom] = useState({});

    useEffect(() => {
        const question = getCurrentQuestion();
        if (!question || !question.kolom) return;

        const kolom = question.kolom;
        const masterData = question.master_data || {};
        const hasData = Object.keys(masterData).length > 0;
        if (hasData && !cachedMasterByKolom[kolom]) {
            setCachedMasterByKolom(prev => ({
                ...prev,
                [kolom]: masterData
            }));
        }
    }, [current.data.soal, current.data.session]);

    // Preload images for current session to avoid slow loading
    useEffect(() => {
        if (current.data.session === null) return;
        const currentSession = session.data[current.data.session];
        if (!currentSession || currentSession.type !== 'exam') return;

        // Collect unique image URLs from all questions in current session
        const urls = new Set();
        (currentSession.content || []).forEach(q => {
            if (q.master_type === 'image') {
                // Reference images from master_data
                const md = q.master_data || {};
                (md.images || []).forEach(img => {
                    if (img.path) urls.add('/' + img.path);
                });
                // Answer option images from pilihan
                (q.pilihan || []).forEach(p => {
                    if (typeof p === 'string' && (p.endsWith('.jpg') || p.endsWith('.png') || p.endsWith('.jpeg'))) {
                        urls.add('/' + p);
                    }
                });
            }
        });

        // Preload all unique images
        urls.forEach(url => {
            const img = new Image();
            img.src = url;
        });
    }, [current.data.session]);

    useEffect(() => {
        if (countdown.duration === 0) {
            if (current.data.session === null) return;
            if (session.data.length === 0) return;
            if (isSubmittingRef.current) return;

            // Timer expired - submit current answers then advance
            finishing();
        }
    }, [countdown.duration]);

    const getCurrentQuestion = () => {
        if (!session.data || session.data.length === 0) return null;
        if (current.data.session === null || current.data.soal === null) return null;
        if (['break', 'instruction'].includes(session.data[current.data.session].type)) return null;

        const question = session.data[current.data.session].content[current.data.soal];
        return question;
    };

    const getMasterType = () => {
        const question = getCurrentQuestion();
        if (question?.master_type) return question.master_type;
        const currentSession = session.data?.[current.data.session];
        if (currentSession?.option_template === 'potensi-kerja-image') return 'image';
        const md = question?.master_data;
        if (md && md.images && Array.isArray(md.images) && md.images.length > 0) return 'image';
        return 'text';
    };

    const getKolom = () => {
        const question = getCurrentQuestion();
        return question?.kolom || '?';
    };

    const getMasterData = () => {
        const question = getCurrentQuestion();
        const kolom = question?.kolom;
        let md = question?.master_data || {};
        if (typeof md === 'string') {
            try { md = JSON.parse(md); } catch (e) { md = {}; }
        }

        let cached = (kolom && cachedMasterByKolom[kolom]) ? cachedMasterByKolom[kolom] : {};
        if (typeof cached === 'string') {
            try { cached = JSON.parse(cached); } catch (e) { cached = {}; }
        }

        let pattern = md.pattern || cached.pattern || md.pola || cached.pola || '';
        let scrambled = md.scrambled || cached.scrambled || '';
        let answerChar = md.answer_char || cached.answer_char || '';

        // Fallback: extract pattern from question.soal if pattern is empty (e.g. Pola: <strong>ABCDE</strong>)
        if (!pattern && typeof question?.soal === 'string') {
            const m = question.soal.match(/Pola:\s*<strong>([^<]+)<\/strong>/i) || question.soal.match(/Pola:\s*([^\s<]+)/i);
            if (m) pattern = m[1].trim();
        }
        // Fallback: extract scrambled from question.soal if scrambled is empty
        if (!scrambled && typeof question?.soal === 'string') {
            const m = question.soal.match(/Pertanyaan:\s*([^<]+)/i);
            if (m) scrambled = m[1].trim();
        }
        // Fallback: if pilihan is array of characters, use that as pattern
        if (!pattern && Array.isArray(question?.pilihan) && question.pilihan.length > 0) {
            pattern = question.pilihan.filter(p => typeof p === 'string' && p.length <= 2).join('');
        }

        return {
            ...cached,
            ...md,
            pattern: pattern,
            images: md.images || cached.images || [],
            scrambled: scrambled,
            answer_char: answerChar
        };
    };

    const answering = (id, jawaban) => {
        // Prevent spam clicking while already transitioning or submitting
        if (isAdvancingRef.current || isSubmittingRef.current) {
            return;
        }
        isAdvancingRef.current = true;
        setIsLocked(true);

        const currentAnswerObj = getStoredAnswer() || answerData || {};
        const newAnswer = { ...currentAnswerObj, [id]: jawaban };

        setAnswerData(newAnswer);
        localStorage.setItem('answer', CryptoJS.AES.encrypt(JSON.stringify(newAnswer), 'secret').toString());

        if (advanceTimeoutRef.current) {
            clearTimeout(advanceTimeoutRef.current);
        }

        // Auto-advance logic with clear visual feedback delay
        advanceTimeoutRef.current = setTimeout(() => {
            advanceTimeoutRef.current = null;
            const currentSession = session.data?.[current.data.session];
            const contentLen = currentSession?.content?.length || 0;

            if ((current.data.soal + 1) >= contentLen) {
                // Last question - submit and move to next session or finish with latest answers
                finishing(newAnswer);
            } else {
                // Move to next question
                isAdvancingRef.current = false;
                setIsLocked(false);
                const nextSoalIndex = current.data.soal + 1;
                const nextSessionData = { ...current.data, soal: nextSoalIndex };
                current.set(nextSessionData);

                try {
                    const rawSess = localStorage.getItem('session');
                    if (rawSess) {
                        const dec = CryptoJS.AES.decrypt(rawSess, 'secret').toString(CryptoJS.enc.Utf8);
                        const parsed = JSON.parse(dec);
                        if (parsed && parsed.session) {
                            parsed.session.soal = nextSoalIndex;
                            localStorage.setItem('session', CryptoJS.AES.encrypt(JSON.stringify(parsed), 'secret').toString());
                        }
                    }
                } catch (e) {}
            }
        }, 450); // 450ms delay so green active feedback is always clearly visible
    };

    const finishing = (explicitAnswer) => {
        // Prevent multiple concurrent finishing calls
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        setIsLocked(true);

        if (advanceTimeoutRef.current) {
            clearTimeout(advanceTimeoutRef.current);
            advanceTimeoutRef.current = null;
        }

        const params = new URLSearchParams(window.location.search);
        const answersToSubmit = explicitAnswer || getStoredAnswer() || answerData || {};
        const mappingToSubmit = getStoredMapping() || mappingData || {};
        const currentSession = session.data?.[current.data.session];

        BaseEndpoint.instance().post(`/exam/${user.version}`, {
            id: params.get('id'),
            token: user.token,
            type: currentSession?.name,
            psychotest_id: currentSession?.psychotest_id,
            answer: answersToSubmit,
            mapping: mappingToSubmit,
        }).then((response) => {
            if (response.status === 200) {
                localStorage.removeItem('answer');
                localStorage.removeItem('mapping');
                setAnswerData(null);
                setMappingData(null);

                let isLastSession = nextSession();
                if (isLastSession === true) {
                    localStorage.removeItem('session');
                    router.push('/app/finish');
                }
            }
        }).catch((error) => {
            console.error('Error saving answers:', error);
            localStorage.removeItem('answer');
            localStorage.removeItem('mapping');
            setAnswerData(null);
            setMappingData(null);

            let isLastSession = nextSession();
            if (isLastSession === true) {
                localStorage.removeItem('session');
                router.push('/app/finish');
            }
        });
    };

    const getProgress = () => {
        if (current.data.session === null || current.data.soal === null) return null;
        const currentSession = session.data[current.data.session];
        if (!currentSession || !currentSession.content) return null;
        return {
            current: current.data.soal + 1,
            total: currentSession.content.length
        };
    };

    /**
     * Render TEXT mode (Matching Gambar 1 cleanly)
     */
    const renderTextMode = () => {
        const question = getCurrentQuestion();
        if (!question) return null;

        const masterData = getMasterData();
        const pattern = masterData.pattern || '';
        const pilihan = question.pilihan || [];
        const progress = getProgress();
        const scrambled = masterData.scrambled || '';
        const patternChars = pattern.split('');
        const currentAnswer = (getStoredAnswer() || answerData)?.[question.id] || null;

        return (
            <div className="mx-0 mt-4 px-0 col-md-12">
                {/* Pola Master Card */}
                <div className="card mb-3">
                    <div className="card-body py-4 text-center">
                        <h3 className="mb-4 font-weight-bold" style={{ fontSize: '2rem', color: '#1e293b' }}>
                            {'Kolom ' + getKolom()}
                        </h3>

                        <div className="d-flex justify-content-center align-items-end flex-nowrap pattern-row-horizontal" style={{ maxWidth: '100%', margin: '0 auto', gap: '20px', overflowX: 'auto', paddingBottom: '6px' }}>
                            {patternChars.map((char, index) => {
                                const letter = String.fromCharCode(65 + index);
                                return (
                                    <div key={index} className="text-center" style={{ minWidth: '70px' }}>
                                        <div style={{
                                            fontSize: '3.5rem',
                                            fontWeight: 800,
                                            lineHeight: 1.1,
                                            color: '#2c3e50',
                                            marginBottom: '8px'
                                        }}>
                                            {char}
                                        </div>
                                        <div style={{
                                            fontSize: '1.8rem',
                                            fontWeight: 700,
                                            color: '#7f8c8d'
                                        }}>
                                            {letter}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Pertanyaan & Pilihan Jawaban Card */}
                <div className="card mb-3">
                    <div className="card-body py-4">
                        <h5 className="card-title border-bottom pb-3 text-muted" style={{ fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Pertanyaan
                        </h5>

                        {/* Deret Pertanyaan (4 Simbol) */}
                        {scrambled ? (() => {
                            const answerChar = masterData.answer_char || '';
                            let displayChars = patternChars.slice();
                            if (answerChar) {
                                const idx = displayChars.indexOf(answerChar);
                                if (idx !== -1) {
                                    displayChars.splice(idx, 1);
                                }
                            } else {
                                displayChars = scrambled.split('').slice(0, 4);
                            }
                            return (
                                <div className="d-flex justify-content-center align-items-center flex-wrap my-4" style={{ maxWidth: '900px', margin: '0 auto', gap: '16px' }}>
                                    {displayChars.map((char, index) => (
                                        <div key={index} className="text-center" style={{ minWidth: '90px' }}>
                                            <div style={{
                                                fontSize: '3.5rem',
                                                fontWeight: 800,
                                                color: '#2c3e50',
                                                padding: '10px 18px',
                                                backgroundColor: '#ffffff',
                                                borderRadius: '12px',
                                                border: '2px dashed #cbd5e1',
                                            }}>
                                                {char}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })() : (
                            <div className="text-center mb-4 p-3 rounded" style={{ fontSize: '1.5rem', color: '#1e293b' }}
                                dangerouslySetInnerHTML={{ __html: question.soal }}>
                            </div>
                        )}

                        {/* PILIHAN JAWABAN (A, B, C, D, E) */}
                        <div className="text-center mt-4 mb-3">
                            <span className="badge px-3 py-1 mb-2" style={{ backgroundColor: '#e2e8f0', color: '#1e293b', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
                                Pilihan Jawaban
                            </span>
                            <p className="text-muted mb-3" style={{ fontSize: '0.95rem' }}>
                                Klik huruf kunci (A, B, C, D, E) yang sesuai dengan simbol yang hilang:
                            </p>
                        </div>

                        <div className="d-flex justify-content-center align-items-center flex-wrap" style={{ maxWidth: '1050px', width: '100%', margin: '0 auto', gap: '16px' }}>
                            {['A', 'B', 'C', 'D', 'E'].map((optionLabel, index) => {
                                const isSelected = currentAnswer === optionLabel;
                                return (
                                    <div key={index} style={{ flex: '1 1 150px', minWidth: '150px', maxWidth: '200px' }}>
                                        <button
                                            type="button"
                                            disabled={isLocked}
                                            onClick={() => answering(question.id, optionLabel)}
                                            className="btn btn-block py-3 px-5 font-weight-bold"
                                            style={{
                                                width: '100%',
                                                fontSize: '2.2rem',
                                                fontWeight: 800,
                                                borderRadius: '12px',
                                                backgroundColor: isSelected ? '#10b981' : '#f56409',
                                                borderColor: isSelected ? '#059669' : '#f56409',
                                                color: '#ffffff',
                                                boxShadow: isSelected ? '0 6px 16px rgba(16, 185, 129, 0.4)' : '0 4px 10px rgba(245, 100, 9, 0.25)',
                                                transition: 'all 0.15s ease',
                                                transform: isSelected ? 'scale(1.05)' : 'none',
                                                pointerEvents: isLocked ? 'none' : 'auto',
                                                opacity: isLocked && !isSelected ? 0.75 : 1
                                            }}
                                        >
                                            {optionLabel}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Status Feedback Banner */}
                        {currentAnswer && (
                            <div className="text-center mt-4">
                                <span className="badge px-4 py-2" style={{ backgroundColor: '#dcfce7', color: '#15803d', fontSize: '0.95rem', fontWeight: 700, borderRadius: '20px' }}>
                                    ✓ Pilihan Terpilih: Huruf {currentAnswer} (Warna hijau menandakan opsi jawaban yang Anda pilih)
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Render IMAGE mode (Matching Gambar 2 concept and structure cleanly)
     */
    const renderImageMode = () => {
        const question = getCurrentQuestion();
        if (!question) return null;

        const masterData = getMasterData();
        const images = masterData.images || [];
        const pilihan = question.pilihan || [];
        const currentAnswer = (getStoredAnswer() || answerData)?.[question.id] || null;

        return (
            <div className="mx-0 mt-4 px-0 col-md-12">
                {/* Pola Master Card */}
                <div className="card mb-3">
                    <div className="card-body py-4 text-center">
                        <h3 className="mb-4 font-weight-bold" style={{ fontSize: '2rem', color: '#1e293b' }}>
                            {'Kolom ' + getKolom()}
                        </h3>

                        <div className="d-flex justify-content-center align-items-end flex-nowrap pattern-row-horizontal" style={{ maxWidth: '100%', margin: '0 auto', gap: '20px', overflowX: 'auto', paddingBottom: '8px' }}>
                            {images.slice(0, 5).map((img, index) => {
                                const letter = img.label || String.fromCharCode(65 + index);
                                return (
                                    <div key={index} className="text-center pattern-item" style={{ flex: '0 0 auto', width: '140px' }}>
                                        <div
                                            className="bg-white rounded border d-flex align-items-center justify-content-center"
                                            style={{
                                                width: '140px',
                                                height: '140px',
                                                padding: '8px',
                                                borderRadius: '12px',
                                                borderColor: '#cbd5e1',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                                                marginBottom: '8px',
                                                overflow: 'hidden',
                                                margin: '0 auto'
                                            }}
                                        >
                                            <img
                                                src={img.path ? (img.path.startsWith('/') ? img.path : `/${img.path}`) : ''}
                                                alt={letter}
                                                className="potensi-kerja-pattern-img"
                                                style={{ maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '/images/placeholder.svg';
                                                }}
                                            />
                                        </div>
                                        <div style={{
                                            fontSize: '2rem',
                                            fontWeight: 800,
                                            color: '#64748b'
                                        }}>
                                            {letter}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Pertanyaan & Pilihan Jawaban Card */}
                <div className="card mb-3">
                    <div className="card-body py-4">
                        <h5 className="card-title border-bottom pb-3 text-muted" style={{ fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Pertanyaan
                        </h5>

                        {/* Deret Gambar Pertanyaan (4 Gambar) */}
                        <div className="d-flex justify-content-center align-items-center flex-nowrap my-4" style={{ maxWidth: '100%', margin: '0 auto', gap: '20px', overflowX: 'auto', paddingBottom: '8px' }}>
                            {pilihan.slice(0, 4).map((optionPath, index) => (
                                <div key={index} className="text-center" style={{ flex: '0 0 auto', width: '140px' }}>
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{
                                            width: '140px',
                                            height: '140px',
                                            padding: '8px',
                                            backgroundColor: '#ffffff',
                                            borderRadius: '12px',
                                            border: '2px dashed #cbd5e1',
                                            overflow: 'hidden',
                                            margin: '0 auto'
                                        }}
                                    >
                                        <img
                                            src={optionPath.startsWith('/') ? optionPath : `/${optionPath}`}
                                            alt={`Question image ${index + 1}`}
                                            className="potensi-kerja-pattern-img"
                                            style={{ maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = '/images/placeholder.svg';
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* PILIHAN JAWABAN (A, B, C, D, E) */}
                        <div className="text-center mt-4 mb-3">
                            <span className="badge px-3 py-1 mb-2" style={{ backgroundColor: '#e2e8f0', color: '#1e293b', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
                                Pilihan Jawaban
                            </span>
                            <p className="text-muted mb-3" style={{ fontSize: '0.95rem' }}>
                                Klik huruf kunci (A, B, C, D, E) yang sesuai dengan gambar yang hilang:
                            </p>
                        </div>

                        <div className="d-flex justify-content-center align-items-center flex-wrap" style={{ maxWidth: '1050px', width: '100%', margin: '0 auto', gap: '16px' }}>
                            {['A', 'B', 'C', 'D', 'E'].map((optionLabel, index) => {
                                const isSelected = currentAnswer === optionLabel;
                                return (
                                    <div key={index} style={{ flex: '1 1 150px', minWidth: '150px', maxWidth: '200px' }}>
                                        <button
                                            type="button"
                                            disabled={isLocked}
                                            onClick={() => answering(question.id, optionLabel)}
                                            className="btn btn-block py-3 px-5 font-weight-bold"
                                            style={{
                                                width: '100%',
                                                fontSize: '2.2rem',
                                                fontWeight: 800,
                                                borderRadius: '12px',
                                                backgroundColor: isSelected ? '#10b981' : '#f56409',
                                                borderColor: isSelected ? '#059669' : '#f56409',
                                                color: '#ffffff',
                                                boxShadow: isSelected ? '0 6px 16px rgba(16, 185, 129, 0.4)' : '0 4px 10px rgba(245, 100, 9, 0.25)',
                                                transition: 'all 0.15s ease',
                                                transform: isSelected ? 'scale(1.05)' : 'none',
                                                pointerEvents: isLocked ? 'none' : 'auto',
                                                opacity: isLocked && !isSelected ? 0.75 : 1
                                            }}
                                        >
                                            {optionLabel}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Status Feedback Banner */}
                        {currentAnswer && (
                            <div className="text-center mt-4">
                                <span className="badge px-4 py-2" style={{ backgroundColor: '#dcfce7', color: '#15803d', fontSize: '0.95rem', fontWeight: 700, borderRadius: '20px' }}>
                                    ✓ Pilihan Terpilih: Huruf {currentAnswer} (Warna hijau menandakan opsi jawaban yang Anda pilih)
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const masterType = getMasterType();
    const currentQuestion = getCurrentQuestion();

    // Check if question data is available
    if (!currentQuestion) {
        return (
            <div className="mx-0 mt-4 px-0 col-md-12">
                <div className="card mb-3">
                    <div className="card-body">
                        <h5 className="card-title text-center">Loading...</h5>
                        <p className="text-center text-muted">
                            Session: {current.data.session}, Question: {current.data.soal}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return masterType === 'image' ? renderImageMode() : renderTextMode();
}
